import React, { createContext, useEffect, useMemo, useReducer, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import jwtDecode from "jwt-decode";
import { actions, useAppState } from "./AppStateContext";
import {
  useGetUserPermissionsQuery,
  useLogoutMutation,
  useMeQuery,
  useValidateSessionQuery,
} from "../graphql/generated/graphql";
import { usePostHog } from "posthog-js/react";
import Sentry from "../utils/sentry";
import returnToUtils from "../utils/returnTo.utils";
import { RolePermissions } from "../constants/rolesAndPermissions";

interface DecodedToken {
  userId: string;
  email: string;
  name: string;
  deviceId: string;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  iat: number;
  exp: number;
}

interface AuthContextType {
  isUpdating: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  login: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyOTPForSession: (accessToken: string, refreshToken: string) => Promise<void>;
  userInfo: {
    userId: string;
    userName: string;
    userInitial: string;
    userRole: UserRoleUI;
    userPermissions: string[];
  };
}

export const decodeToken = async (token: string) => {
  const decoded = jwtDecode<DecodedToken>(token);
  return decoded;
};

export enum UserRoleUI {
  STUDENT = "Student",
  FACULTY = "Faculty",
  AUDITOR = "Auditor",
  SCHOOL_ADMIN = "School Admin",
  ADMIN = "Admin",
}

export const USER_ROLE_RANK: Record<UserRoleUI, number> = {
  [UserRoleUI.STUDENT]: 0,
  [UserRoleUI.FACULTY]: 1,
  [UserRoleUI.AUDITOR]: 2,
  [UserRoleUI.SCHOOL_ADMIN]: 3,
  [UserRoleUI.ADMIN]: 4,
};

export const isBelowMinRole = (actual: UserRoleUI, required: UserRoleUI) =>
  USER_ROLE_RANK[actual] < USER_ROLE_RANK[required];

export const hasMinRole = (actual: UserRoleUI, required: UserRoleUI) =>
  USER_ROLE_RANK[actual] >= USER_ROLE_RANK[required];

export const AuthContext = createContext<AuthContextType>({
  isUpdating: false,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  login: async () => {},
  logout: async () => {},
  verifyOTPForSession: async () => {},
  userInfo: {
    userId: "",
    userName: "",
    userInitial: "",
    userRole: UserRoleUI.STUDENT,
    userPermissions: [],
  },
});

const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_AUTH_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const AuthProvider = ({ children, apolloClient }) => {
  const [isUpdating, setIsUpdating] = useState(true);
  const [state, dispatch] = useReducer(authReducer, {
    isLoading: true,
    isAuthenticated: false,
    isEmailVerified: false,
    otpVerifiedForSession: false,
  });

  const { dispatch: appDispatch } = useAppState();
  const posthog = usePostHog();

  const { data: userPermissionsData } = useGetUserPermissionsQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const { data: meData } = useMeQuery({
    skip: !state.isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const userRole = useMemo(() => {
    if (userPermissionsData?.getUserPermissions.includes(RolePermissions.wildcard)) {
      return UserRoleUI.ADMIN;
    }
    // Admin has faculty:edit permission, Faculty does not
    if (userPermissionsData?.getUserPermissions.includes(RolePermissions.facultyEdit)) {
      return UserRoleUI.SCHOOL_ADMIN;
    }
    if (userPermissionsData?.getUserPermissions.includes(RolePermissions.analyticsViewSchool)) {
      return UserRoleUI.AUDITOR;
    }
    // Faculty has course:edit but not faculty:edit
    if (userPermissionsData?.getUserPermissions.includes(RolePermissions.courseEdit)) {
      return UserRoleUI.FACULTY;
    }
    return UserRoleUI.STUDENT; // fallback, shouldn't happen in admin layout
  }, [userPermissionsData?.getUserPermissions]);

  const userInfo = useMemo(() => {
    return {
      userId: meData?.me?.id || "",
      userName: meData?.me?.name || "",
      userInitial: meData?.me?.name.charAt(0) || "",
      userRole: userRole,
      userPermissions: userPermissionsData?.getUserPermissions || [],
    };
  }, [meData, userPermissionsData?.getUserPermissions]);

  const { data, loading, startPolling, stopPolling } = useValidateSessionQuery({
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !state.isAuthenticated,
  });

  const [logoutMutation] = useLogoutMutation();

  useEffect(() => {
    const checkToken = async () => {
      const accessToken = await AsyncStorage.getItem("accessToken");
      const refreshToken = await AsyncStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        const decodedAccessToken = await decodeToken(accessToken);

        posthog?.identify(decodedAccessToken.userId);
        dispatch({
          type: "SET_AUTH_STATE",
          payload: {
            isLoading: false,
            isAuthenticated: true,
            isEmailVerified: decodedAccessToken.isEmailVerified,
            otpVerifiedForSession: decodedAccessToken.otpVerifiedForSession,
          },
        });
      } else {
        posthog?.reset(true);
        dispatch({
          type: "SET_AUTH_STATE",
          payload: {
            isLoading: false,
            isAuthenticated: false,
            isEmailVerified: false,
            otpVerifiedForSession: false,
          },
        });
        setIsUpdating(false);
      }
    };

    if (isUpdating) {
      checkToken();
    }
    setIsUpdating(false);
  }, [isUpdating]);

  useEffect(() => {
    const checkSession = async () => {
      if (data && !data.validateSession.isValid) {
        posthog?.reset(true);
      }
    };

    checkSession();
  }, [data]);

  useEffect(() => {
    const pollInterval = 60000;
    startPolling(pollInterval);

    return () => {
      stopPolling();
    };
  }, []);

  const login = async (accessToken, refreshToken) => {
    setIsUpdating(true);

    try {
      await AsyncStorage.setItem("accessToken", accessToken);
      await AsyncStorage.setItem("refreshToken", refreshToken);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const logout = async () => {
    setIsUpdating(true);

    dispatch({
      type: "SET_AUTH_STATE",
      payload: {
        isLoading: false,
        isAuthenticated: false,
        isEmailVerified: false,
        otpVerifiedForSession: false,
      },
    });

    await AsyncStorage.removeItem("accessToken");
    await AsyncStorage.removeItem("refreshToken");

    try {
      const result = await logoutMutation();

      if (!result?.data?.logout?.success) {
        throw new Error("Failed to logout");
      }
      // Clear any stored returnTo URL so post-login does not redirect
      await returnToUtils.clearReturnToUrl();

      if (apolloClient) {
        await apolloClient.clearStore();
      }

      setIsUpdating(false);

      appDispatch({
        type: actions.RESET_STATE,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const verifyOTPForSession = async (accessToken, refreshToken) => {
    try {
      await AsyncStorage.setItem("accessToken", accessToken);
      await AsyncStorage.setItem("refreshToken", refreshToken);
      setIsUpdating(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  if (loading || state.isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        isUpdating,
        isAuthenticated: state.isAuthenticated,
        isEmailVerified: state.isEmailVerified,
        otpVerifiedForSession: state.otpVerifiedForSession,
        login,
        logout,
        verifyOTPForSession,
        userInfo,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
